export function getExtensionFromMimeType(mimetype: string): string {
  switch (mimetype) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'JPG';
    case 'image/png':
      return 'PNG';
    case 'image/gif':
      return 'GIF';
    case 'image/webp':
      return 'WEBP';
    case 'image/heic':
      return 'HEIC';
    case 'image/heif':
      return 'HEIF';
    case 'application/pdf':
      return 'PDF';
    case 'application/msword':
      return 'DOC';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'DOCX';
    case 'application/vnd.ms-excel':
      return 'XLS';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'XLSX';
    case 'application/vnd.ms-powerpoint':
      return 'PPT';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'PPTX';
    case 'application/vnd.apple.keynote':
    case 'application/x-iwork-keynote-sffkey':
      return 'KEY';
    case 'text/plain':
      return 'TXT';
    case 'text/html':
      return 'HTML';
    default:
      return 'OTHER';
  }
}

export function isPresentationMimetype(mimetype: string): boolean {
  switch (mimetype) {
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.apple.keynote':
    case 'application/vnd.google-apps.presentation':
    case 'application/x-iwork-keynote-sffkey':
      return true;
    default:
      return false;
  }
}

// for material attachments
export const SupportedMimeTypes = [
  'application/msword',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'text/html',
  'text/plain',
];

/**
 * The mime type a file's own name implies, for the browsers that report none.
 *
 * iOS hands over a `File` with an empty `type` for a good deal of what the
 * photo library and Files app offer, and a Windows share can do the same — so
 * a perfectly readable PDF arrives looking like nothing at all. The extension
 * is the only thing left to go on, and `undefined` when even that says
 * nothing.
 */
export function mimeTypeFromFileName(fileName: string): string | undefined {
  const extension = fileName
    .slice(fileName.lastIndexOf('.') + 1)
    .toLowerCase()
    .trim();
  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    case 'htm':
    case 'html':
      return 'text/html';
    case 'txt':
      return 'text/plain';
    default:
      return undefined;
  }
}

/**
 * The file, with a mime type it can be judged and uploaded by: its own when
 * the browser gave it one, its extension's otherwise. Returned unchanged when
 * neither says anything — the caller then refuses it, which is the honest
 * answer for a file nothing can identify.
 */
export function withResolvedMimeType(file: File): File {
  if (file.type) return file;
  const mimeType = mimeTypeFromFileName(file.name);
  if (!mimeType) return file;
  return new File([file], file.name, { type: mimeType, lastModified: file.lastModified });
}

// for material attachments
export function isFileMimetypeSupported(mimetype: string): boolean {
  switch (mimetype) {
    case 'application/msword':
    case 'application/pdf':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'image/jpeg':
    case 'image/jpg':
    case 'image/png':
    case 'image/webp':
    case 'text/html':
    case 'text/plain':
      return true;
    case 'image/heic': // ='( https://github.com/docling-project/docling/issues/787
    case 'image/heif':
    default:
      return false;
  }
}
